import SEO, { PAGE_SEO } from '../components/SEO';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { BookOpen, Sparkles, Clock, Search, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 6;

// Helper to get SEO-friendly content URL
const getContentUrl = (item) => `/content/${item.slug || item.content_id}`;

// Article fallback images pool
const ARTICLE_IMAGES = [
  'https://images.pexels.com/photos/3785147/pexels-photo-3785147.jpeg',
  'https://images.pexels.com/photos/3762879/pexels-photo-3762879.jpeg',
  'https://images.pexels.com/photos/3997379/pexels-photo-3997379.jpeg',
  'https://images.pexels.com/photos/5069432/pexels-photo-5069432.jpeg',
  'https://images.pexels.com/photos/3768163/pexels-photo-3768163.jpeg',
  'https://images.pexels.com/photos/4041392/pexels-photo-4041392.jpeg',
  'https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg',
  'https://images.pexels.com/photos/3993320/pexels-photo-3993320.jpeg',
];

export default function DeepDives() {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [displayedArticles, setDisplayedArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    setDisplayedArticles(filteredArticles.slice(0, ITEMS_PER_PAGE));
    setHasMore(filteredArticles.length > ITEMS_PER_PAGE);
  }, [filteredArticles]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/content?content_type=article&limit=100`);
      const data = await res.json();
      setArticles(data);
      setFilteredArticles(data);
      
      const cats = [...new Set(data.map(item => item.category))];
      setCategories(cats);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    setTimeout(() => {
      const currentLength = displayedArticles.length;
      const nextItems = filteredArticles.slice(currentLength, currentLength + ITEMS_PER_PAGE);
      setDisplayedArticles(prev => [...prev, ...nextItems]);
      setHasMore(currentLength + nextItems.length < filteredArticles.length);
      setLoadingMore(false);
    }, 300);
  }, [displayedArticles, filteredArticles, loadingMore, hasMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore, hasMore, loadingMore]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setFilteredArticles(articles);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/content/search?q=${encodeURIComponent(searchQuery)}&content_type=article`);
      const data = await res.json();
      setFilteredArticles(data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, articles]);

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    if (!category) {
      setFilteredArticles(articles);
    } else {
      setFilteredArticles(articles.filter(item => item.category === category));
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setFilteredArticles(articles);
  };

  const getItemImage = (item, index) => {
    if (item.image_url) return item.image_url;
    return ARTICLE_IMAGES[index % ARTICLE_IMAGES.length];
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="deep-dives-page">
      <SEO {...PAGE_SEO.deepDives} url="/deep-dives" />
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 rounded-sm mb-4">
              <BookOpen className="w-4 h-4 text-gold" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gold">Articles</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold mb-4">Deep Dives</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              In-depth guides, expert analysis, and comprehensive articles on skin and hair care.
            </p>
          </div>

          {/* Search & Filters */}
          <div className="mb-8 space-y-4">
            <div className="flex gap-2 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                  data-testid="article-search-input"
                />
              </div>
              <Button onClick={handleSearch} className="btn-primary" data-testid="article-search-btn">
                Search
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant={!selectedCategory ? "default" : "outline"}
                size="sm"
                onClick={() => handleCategoryFilter('')}
                className={!selectedCategory ? 'bg-gold text-black hover:bg-gold/90' : ''}
              >
                All
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleCategoryFilter(cat)}
                  className={selectedCategory === cat ? 'bg-gold text-black hover:bg-gold/90' : ''}
                >
                  {cat}
                </Button>
              ))}
              {(searchQuery || selectedCategory) && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                  <X className="w-3 h-3" /> Clear
                </Button>
              )}
            </div>
          </div>

          {(searchQuery || selectedCategory) && (
            <p className="text-sm text-muted-foreground mb-6">
              Showing {filteredArticles.length} result{filteredArticles.length !== 1 ? 's' : ''}
            </p>
          )}

          {/* Articles Grid */}
          {loading ? (
            <div className="space-y-8">
              {[...Array(4)].map((_, i) => (
                <Card key={i} className="border-none">
                  <div className="grid md:grid-cols-3 gap-6">
                    <Skeleton className="aspect-video" />
                    <div className="md:col-span-2 p-6">
                      <Skeleton className="h-4 w-24 mb-4" />
                      <Skeleton className="h-8 w-full mb-4" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-16">
              <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No articles found. Try a different search term.</p>
            </div>
          ) : (
            <>
              <div className="space-y-6">
                {displayedArticles.map((item, index) => (
                  <Link 
                    key={item.content_id} 
                    to={getContentUrl(item)}
                    className="group block"
                    data-testid={`article-item-${item.content_id}`}
                  >
                    <Card className="bg-card border-none shadow-card card-hover overflow-hidden">
                      <div className="grid md:grid-cols-3 gap-0">
                        <div className="aspect-video md:aspect-auto overflow-hidden">
                          <img
                            src={getItemImage(item, index)}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="md:col-span-2 p-6 md:p-8 flex flex-col justify-center">
                          <div className="flex items-center gap-4 mb-3 text-xs text-muted-foreground">
                            <span className="text-gold font-semibold uppercase tracking-wider">{item.category}</span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {item.read_time} min read
                            </span>
                          </div>
                          <h2 className="font-serif text-xl md:text-2xl font-semibold mb-3 group-hover:text-gold transition-colors">
                            {item.title}
                          </h2>
                          <p className="text-muted-foreground line-clamp-2 mb-4">{item.summary}</p>
                          <div className="flex flex-wrap gap-2">
                            {item.tags?.slice(0, 4).map((tag, i) => (
                              <span key={i} className="px-2 py-1 bg-secondary text-xs rounded-sm">{tag}</span>
                            ))}
                            <span className="flex items-center gap-1 px-2 py-1 bg-gold/10 rounded-sm ml-auto">
                              <Sparkles className="w-3 h-3 text-gold" />
                              <span className="text-xs font-semibold text-gold">+{item.points_reward} pts</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>

              {/* Load More Section */}
              <div ref={loaderRef} className="flex justify-center py-8">
                {loadingMore ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Loading more...</span>
                  </div>
                ) : hasMore ? (
                  <Button 
                    onClick={loadMore} 
                    variant="outline" 
                    className="px-8"
                    data-testid="load-more-btn"
                  >
                    Load More Articles
                  </Button>
                ) : displayedArticles.length > 0 ? (
                  <p className="text-sm text-muted-foreground">You've reached the end</p>
                ) : null}
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
